<template>
  <div class="text-center">
    <v-menu v-model="menu" :close-on-content-click="false" bottom offset-y>
      <template v-slot:activator="{ props }">
        <v-chip :color="readableName() ? 'primary' : 'primary'" v-bind="props" label>
          {{ $t("page.payment.amount") }}
          <span v-if="readableName()">&nbsp;|&nbsp;</span>
          <span class="font-weight-bold"> {{ readableName() }}</span>
          <v-btn
            v-if="readableName()"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset(true)"
          />
        </v-chip>
      </template>

      <v-card min-width="350" class="mt-2">
        <v-list>
          <v-list-item :title="$t('page.payment.filter.filterByAmount')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-list class="mt-1">
          <v-row class="mx-4" no-gutters>
            <v-btn variant="text" size="x-small" color="primary" @click="reset(false)">{{
              $t("common.action.reset")
            }}</v-btn>
          </v-row>

          <v-row class="mx-6 mt-3" no-gutters>
            <v-text-field
              v-model="minAmount"
              class="my-0 py-0"
              :rules="amountFilterRules"
              :label="$t('page.payment.filter.minAmount')"
              :options="{
                currency: useAccountStore().getDefaultCurrency,
              }"
            />
            <v-icon icon="mdi-arrow-right-thin" class="mt-3 ma-1" />

            <v-text-field
              v-model="maxAmount"
              class="my-0 py-0"
              :rules="amountFilterRules"
              :label="$t('page.payment.filter.maxAmount')"
              :options="{
                currency: useAccountStore().getDefaultCurrency,
              }"
            />
          </v-row>
        </v-list>

        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="
              menu = false;
              updateFilterPayments();
            "
            color="primary"
            class="mr-2"
          >
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import i18n from "@/app/plugins/i18n";
import { useAccountStore } from "@/account/stores/account";
import { usePaymentStore } from "@/payments/stores/payments";
import { useValidation } from "@/shared/composables/validation";

const menu = ref(false);
const { amountFilterRules } = useValidation();
const { setFilterPaymentCondition } = usePaymentStore();

const paymentStore = usePaymentStore();
const filteredMinAmount = computed(() => paymentStore.filterPaymentCondition.minAmount || "");
const filteredMaxAmount = computed(() => paymentStore.filterPaymentCondition.maxAmount || "");

const minAmount = ref();
const maxAmount = ref();

const reset = async (clear?: boolean) => {
  minAmount.value = null;
  maxAmount.value = null;
  const filterPaymentCondition = usePaymentStore().filterPaymentCondition;
  delete filterPaymentCondition.minAmount;
  delete filterPaymentCondition.maxAmount;
  if (clear) {
    await usePaymentStore().getFilteredPayments();
  }
  setFilterPaymentCondition(filterPaymentCondition);
};

const updateFilterPayments = () => {
  const oldCondition = usePaymentStore().filterPaymentCondition;
  const newCondition = {
    ...oldCondition,
    minAmount: Number(minAmount.value),
    maxAmount: Number(maxAmount.value),
  };
  setFilterPaymentCondition(newCondition);
  minAmount.value = null;
  maxAmount.value = null;
};

const readableName = () => {
  let name = "";

  if (filteredMinAmount.value && filteredMaxAmount.value) {
    if (filteredMinAmount.value === filteredMaxAmount.value) {
      name = ` ${i18n.global.t("page.payment.filter.isEqualTo")} ${filteredMinAmount.value}`;
    } else if (filteredMinAmount.value < filteredMaxAmount.value) {
      name = `${i18n.global.t("page.payment.filter.isBetween")} ${
        filteredMinAmount.value
      } and ${filteredMaxAmount.value}`;
    }
  } else if (filteredMinAmount.value) {
    name = ` ${i18n.global.t("page.payment.filter.isGreatThan")} ${filteredMinAmount.value}`;
  } else if (filteredMaxAmount.value) {
    name = ` ${i18n.global.t("page.payment.filter.isLessThan")} ${filteredMaxAmount.value}`;
  }

  return name;
};
</script>
